/**
 * Money / progress helpers shared between StudentDetail and GroupDetail
 * «control center»-style pages.
 *
 * Originally lived in `StudentDetail/parts/format.ts`; promoted here so
 * the same `formatMoneyCurrency` / `paidPercent` / `progressTone` can
 * power both the per-student and per-group KPI/roster rendering.
 */

const NUMBER_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// `CustomFloat` is typed `any` in the generated schema and Django
// sometimes serialises Decimals as strings — coerce defensively so a
// callsite that accidentally `+=`-concatenated values still renders a
// number instead of "NaN PLN".
const toFiniteNumber = (n: unknown): number | null => {
  if (n === null || n === undefined || n === '') return null;
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(num) ? num : null;
};

export const formatMoney = (n: number | null | undefined): string => {
  const num = toFiniteNumber(n);
  if (num === null) return '—';
  return NUMBER_FMT.format(num);
};

export const formatMoneyCurrency = (
  n: number | null | undefined,
  currency: string | null | undefined,
): string => {
  const num = toFiniteNumber(n);
  if (num === null) return '—';
  return `${NUMBER_FMT.format(num)} ${currency || ''}`.trim();
};

/** 0..100 — used for the progress track inside an agreement card. */
export const paidPercent = (
  budget: number | null | undefined,
  paid: number | null | undefined,
): number => {
  if (!budget || budget <= 0) return 0;
  return ((paid || 0) / budget) * 100;
};

export const progressTone = (pct: number): 'ok' | 'warn' | 'danger' => {
  if (pct >= 100) return 'ok';
  if (pct >= 60) return 'warn';
  return 'danger';
};

/**
 * Strip the backend's machine `[reason=...]` tag from an agreement `comment`.
 *
 * `terminateAgreement` stores the termination reason inline in the free-text
 * `comment` with a `[reason=non_payment]` prefix (a backend product decision —
 * no dedicated column). Any UI that renders the raw comment to an operator must
 * hide that tag so it doesn't leak as user-facing text. Returns the trimmed,
 * tag-free comment (empty string for nullish input).
 */
export const stripReasonTag = (comment: string | null | undefined): string =>
  (comment || '').replace(/\[reason=[^\]]+\]\s*/g, '').trim();
